import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  Button,
  Chip,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  AccountTree as HealthTreeIcon,
  Lock as LockIcon,
  Assessment as ReportIcon,
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as UncheckedIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  EmojiEvents as TrophyIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../contexts/ThemeContext';

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  targetPage: string;
  actionText: string;
  completed: boolean;
}

interface OnboardingProgressProps {
  hasVisitedHealthTree: boolean;
  hasCreatedLockIn: boolean;
  hasReportedProgress: boolean;
  onStartTour: () => void;
}

const OnboardingProgress: React.FC<OnboardingProgressProps> = ({
  hasVisitedHealthTree,
  hasCreatedLockIn,
  hasReportedProgress,
  onStartTour,
}) => {
  const navigate = useNavigate();
  const { mode } = useThemeMode();
  const [expanded, setExpanded] = React.useState(true);

  const steps: OnboardingStep[] = [
    {
      id: 'explore',
      title: 'Explore Health Decision Tree',
      description: 'Browse the health choices and see what you can develop',
      icon: <HealthTreeIcon />,
      targetPage: '/talent-tree',
      actionText: 'Explore Now',
      completed: hasVisitedHealthTree,
    },
    {
      id: 'lockin',
      title: 'Create Weekly Commitment',
      description: 'Choose which health talents to focus on this week',
      icon: <LockIcon />,
      targetPage: '/weekly-lockin',
      actionText: 'Commit',
      completed: hasCreatedLockIn,
    },
    {
      id: 'report',
      title: 'Report Progress',
      description: 'Track your daily activities and build consistency',
      icon: <ReportIcon />,
      targetPage: '/weekly-lockin',
      actionText: 'Track Progress',
      completed: hasReportedProgress,
    },
  ];

  const completedSteps = steps.filter(step => step.completed).length;
  const totalSteps = steps.length;
  const progressPercentage = (completedSteps / totalSteps) * 100;
  const isAllComplete = completedSteps === totalSteps;

  const handleStepAction = (step: OnboardingStep) => {
    navigate(step.targetPage);
  };

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  return (
    <Card
      sx={{
        background: mode === 'dark' 
          ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)'
          : 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(16, 185, 129, 0.02) 100%)',
        border: mode === 'dark' ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(16, 185, 129, 0.2)',
        borderRadius: 3,
        overflow: 'visible',
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {isAllComplete ? (
              <TrophyIcon sx={{ color: '#f59e0b', fontSize: 28 }} />
            ) : (
              <HealthTreeIcon sx={{ color: '#10b981', fontSize: 28 }} />
            )}
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  color: mode === 'dark' ? '#ffffff' : '#1f2937',
                  mb: 0.5,
                }}
              >
                {isAllComplete ? "You're a Pro Now! 🎉" : 'Getting Started Guide'}
              </Typography>
              {!isAllComplete && (
                <Typography
                  variant="body2"
                  sx={{
                    color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(31, 41, 55, 0.7)',
                  }}
                >
                  Complete these steps to master Health-Tree
                </Typography>
              )}
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!isAllComplete && (
              <Chip
                label={`${completedSteps}/${totalSteps}`}
                size="small"
                sx={{
                  backgroundColor: '#10b981',
                  color: 'white',
                  fontWeight: 'bold',
                }}
              />
            )}
            <IconButton onClick={toggleExpanded} size="small">
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* Completion Message */}
        {isAllComplete && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body1"
              sx={{
                color: mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(31, 41, 55, 0.8)',
                textAlign: 'center',
                fontWeight: 500,
              }}
            >
              Congratulations! You've completed all the essential steps. You now know how to explore, commit, and track your health journey.
            </Typography>
          </Box>
        )}

        {/* Progress Bar */}
        {!isAllComplete && (
          <Box sx={{ mb: 3 }}>
            <LinearProgress
              variant="determinate"
              value={progressPercentage}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#10b981',
                  borderRadius: 4,
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(31, 41, 55, 0.6)',
                mt: 1,
                display: 'block',
              }}
            >
              {Math.round(progressPercentage)}% Complete
            </Typography>
          </Box>
        )}

        {/* Steps */}
        <Collapse in={expanded}>
          <Box sx={{ space: 2 }}>
            {steps.map((step, index) => (
              <Box
                key={step.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: step.completed
                    ? mode === 'dark' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.05)'
                    : mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                  border: step.completed
                    ? mode === 'dark' ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(16, 185, 129, 0.2)'
                    : mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
                  mb: index < steps.length - 1 ? 2 : 0,
                  transition: 'all 0.3s ease',
                }}
              >
                {/* Step Icon and Check */}
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                  {step.completed ? (
                    <CheckIcon sx={{ color: '#10b981', fontSize: 24 }} />
                  ) : (
                    <UncheckedIcon sx={{ color: mode === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)', fontSize: 24 }} />
                  )}
                </Box>

                {/* Step Content */}
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 'bold',
                      color: step.completed 
                        ? '#10b981' 
                        : mode === 'dark' ? '#ffffff' : '#1f2937',
                      mb: 0.5,
                    }}
                  >
                    {step.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(31, 41, 55, 0.7)',
                    }}
                  >
                    {step.description}
                  </Typography>
                </Box>

                {/* Action Button */}
                {!step.completed && (
                  <Button
                    onClick={() => handleStepAction(step)}
                    variant="contained"
                    size="small"
                    sx={{
                      backgroundColor: '#10b981',
                      '&:hover': {
                        backgroundColor: '#059669',
                      },
                      borderRadius: 2,
                      px: 2,
                      py: 0.5,
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                    }}
                  >
                    {step.actionText}
                  </Button>
                )}
              </Box>
            ))}
          </Box>
        </Collapse>

        {/* Actions */}
        {!isAllComplete && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              onClick={onStartTour}
              variant="outlined"
              sx={{
                borderColor: '#10b981',
                color: '#10b981',
                '&:hover': {
                  borderColor: '#059669',
                  backgroundColor: 'rgba(16, 185, 129, 0.05)',
                },
                borderRadius: 2,
                px: 3,
                py: 1,
                fontWeight: 'bold',
              }}
            >
              Take the Tour
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default OnboardingProgress;